export enum ErrorSeverity {
  INFO = 'info',
  WARNING = 'warning',
  ERROR = 'error',
  CRITICAL = 'critical',
}

export interface ErrorAction {
  label: string;
  handler: () => void;
}

export interface VideoError {
  severity: ErrorSeverity;
  title: string;
  message: string;
  actions?: ErrorAction[];
  canContinue: boolean;
  technicalDetails?: string;
}

export interface ErrorContext {
  operation: string;
  deviceLabel?: string;
  deviceId?: string;
  retryHandler?: () => void;
  continueHandler?: () => void;
  helpUrl?: string;
}

export const classifyError = (error: any, context: ErrorContext): VideoError => {
  const errorName = error?.name || '';
  const errorType = error?.type || '';
  const errorReason = error?.reason || '';
  const errorMessage = error?.message || '';

  // Permission errors - Most critical
  if (errorName === 'NotAllowedError' || errorType === 'VIDEO_USER_FORBIDDEN_CAPTURE') {
    return {
      severity: ErrorSeverity.CRITICAL,
      title: 'Permission Denied',
      message: `Access to your ${context.deviceLabel || 'camera/microphone'} was denied. Please allow browser permissions and refresh the page to continue.`,
      actions: context.helpUrl
        ? [
            {
              label: 'How to enable permissions',
              handler: () => window.open(context.helpUrl!, '_blank'),
            },
          ]
        : [],
      canContinue: false,
      technicalDetails: errorName,
    };
  }

  // Device not found errors
  if (
    errorName === 'NotFoundError' ||
    errorType === 'CAN_NOT_DETECT_CAMERA' ||
    errorType === 'CAN_NOT_FIND_CAMERA'
  ) {
    const deviceType = context.operation.toLowerCase().includes('camera') ? 'camera' : 'microphone';
    return {
      severity: ErrorSeverity.ERROR,
      title: `No ${deviceType === 'camera' ? 'Camera' : 'Microphone'} Found`,
      message: `No ${deviceType} was detected. You can continue the meeting without ${deviceType === 'camera' ? 'video' : 'audio'}, or connect a ${deviceType} and try again.`,
      actions: [
        ...(context.retryHandler
          ? [
              {
                label: 'Retry',
                handler: context.retryHandler,
              },
            ]
          : []),
        ...(context.continueHandler
          ? [
              {
                label: `Continue without ${deviceType === 'camera' ? 'video' : 'audio'}`,
                handler: context.continueHandler,
              },
            ]
          : []),
      ],
      canContinue: true,
      technicalDetails: errorType || errorName,
    };
  }

  // Device in use errors
  if (errorName === 'NotReadableError' || errorType === 'VIDEO_CAMERA_IS_TAKEN') {
    const deviceType = context.operation.toLowerCase().includes('camera') ? 'camera' : 'microphone';
    return {
      severity: ErrorSeverity.ERROR,
      title: `${deviceType === 'camera' ? 'Camera' : 'Microphone'} In Use`,
      message: `Your ${context.deviceLabel || deviceType} is being used by another application. Close other apps using your ${deviceType} and try again.`,
      actions: context.retryHandler
        ? [
            {
              label: 'Retry',
              handler: context.retryHandler,
            },
          ]
        : [],
      canContinue: true,
      technicalDetails: errorType || errorName,
    };
  }

  // Connection/Stream errors
  if (
    errorType === 'VIDEO_ESTABLISH_STREAM_ERROR' ||
    errorReason?.includes('WebSocket') ||
    errorReason?.includes('network')
  ) {
    return {
      severity: ErrorSeverity.CRITICAL,
      title: 'Connection Failed',
      message:
        'Unable to establish video connection. Please check your internet connection and try refreshing the page.',
      actions: [
        {
          label: 'Refresh Page',
          handler: () => window.location.reload(),
        },
      ],
      canContinue: false,
      technicalDetails: errorType || errorReason,
    };
  }

  // All devices failed
  if (errorType === 'NO_WORKING_MICROPHONE') {
    return {
      severity: ErrorSeverity.CRITICAL,
      title: 'No Working Microphone',
      message:
        'Unable to start any microphone. Please check your device connections, browser permissions, and ensure no other application is using your microphone.',
      actions: context.retryHandler
        ? [
            {
              label: 'Retry',
              handler: context.retryHandler,
            },
          ]
        : [],
      canContinue: true,
      technicalDetails: 'All microphones failed to initialize',
    };
  }

  if (errorType === 'NO_WORKING_CAMERA') {
    return {
      severity: ErrorSeverity.CRITICAL,
      title: 'No Working Camera',
      message:
        'Unable to start any camera. Please check your device connections, browser permissions, and ensure no other application is using your camera.',
      actions: context.retryHandler
        ? [
            {
              label: 'Retry',
              handler: context.retryHandler,
            },
          ]
        : [],
      canContinue: true,
      technicalDetails: 'All cameras failed to initialize',
    };
  }

  // Virtual background not supported
  if (errorReason === 'Virtual background is not supported') {
    return {
      severity: ErrorSeverity.WARNING,
      title: 'Virtual Background Unavailable',
      message: 'Virtual background is not supported on this device. Starting video without it.',
      canContinue: true,
      technicalDetails: errorReason,
    };
  }

  // Screen share denied
  if (errorReason === 'user deny screen share') {
    return {
      severity: ErrorSeverity.INFO,
      title: 'Screen Share Cancelled',
      message: 'Screen sharing was cancelled.',
      canContinue: true,
      technicalDetails: errorReason,
    };
  }

  // Generic device switching error
  if (context.operation.includes('switch') || context.operation.includes('change')) {
    return {
      severity: ErrorSeverity.ERROR,
      title: `Failed to Switch ${context.deviceLabel || 'Device'}`,
      message: `Could not switch to ${context.deviceLabel || 'the selected device'}. The device may be in use by another application or disconnected.`,
      actions: context.retryHandler
        ? [
            {
              label: 'Try Again',
              handler: context.retryHandler,
            },
          ]
        : [],
      canContinue: true,
      technicalDetails: errorMessage || errorReason || errorType,
    };
  }

  // Default fallback
  return {
    severity: ErrorSeverity.ERROR,
    title: `${context.operation} Failed`,
    message:
      errorMessage ||
      errorReason ||
      'An unexpected error occurred. Please try again or refresh the page.',
    actions: context.retryHandler
      ? [
          {
            label: 'Retry',
            handler: context.retryHandler,
          },
        ]
      : [],
    canContinue: true,
    technicalDetails: JSON.stringify({ errorName, errorType, errorReason, errorMessage }),
  };
};

export const getErrorToastMessage = (videoError: VideoError): string => {
  return videoError.message;
};

export const shouldShowErrorModal = (videoError: VideoError): boolean => {
  return videoError.severity === ErrorSeverity.CRITICAL || videoError.actions !== undefined;
};
